import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { accountApi } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';

const createCreditAnalysisSchema = z.object({
  accountId: z.string().min(1, 'Cliente é obrigatório'),
});

type CreateCreditAnalysisFormData = z.infer<typeof createCreditAnalysisSchema>;

export interface UseCreditAnalysisCreateFormOptions {
  onSuccess?: (creditAnalysis: CreditAnalysis) => void;
}

export function useCreditAnalysisCreateForm({
  onSuccess,
}: UseCreditAnalysisCreateFormOptions = {}) {
  const { formError, showFormError, clearFormError } = useFormError();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');

  const form = useForm<CreateCreditAnalysisFormData>({
    resolver: zodResolver(createCreditAnalysisSchema),
    defaultValues: {
      accountId: '',
    },
  });

  const { data: searchResponse, isFetching: isSearching } = useQuery({
    queryKey: ['accounts', 'search', searchQuery],
    queryFn: () => accountApi.findMany({ search: searchQuery, limit: 20 }),
    enabled: searchQuery.length > 0,
  });

  const accountSearchResults = searchResponse?.docs || [];

  const mutation = useMutation({
    mutationFn: async (data: CreateCreditAnalysisFormData) => {
      // Don't send status - let backend determine it based on business logic
      return await creditAnalysisApi.create({
        proposalId: data.accountId,
        accountId: data.accountId,
      });
    },
    onSuccess: (creditAnalysis) => {
      queryClient.invalidateQueries({ queryKey: ['creditAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Análise de crédito criada com sucesso!',
      });
      onSuccess?.(creditAnalysis);
      form.reset();
      setSearchQuery('');
    },
    onError: () => {
      showFormError('Falha ao criar análise de crédito.');
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    mutation.mutate(data);
  });

  const debouncedSetSearchQuery = useMemo(
    () => debounce((value: string) => setSearchQuery(value), 500),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSetSearchQuery(value);
    },
    [debouncedSetSearchQuery],
  );

  return {
    formError,
    clearFormError,
    form,
    handleSubmit,
    isSubmitting: mutation.isPending,
    accountSearchResults,
    isSearching,
    handleSearchChange,
    resetForm: () => {
      form.reset();
      setSearchQuery('');
    },
  };
}
